import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useSeo } from "../lib/seo";
import { getTours } from "../lib/adventureApi";
import RALayout from "../components/ra/RALayout";
import TourCard from "../components/ra/TourCard";

export const CYCLING_INTRO = [
  "Nepal is an extraordinary country to explore by bicycle. From the winding backroads and terraced hillsides of the Kathmandu Valley to the dramatic mountain landscapes of Mustang and the Annapurna region, every ride offers a different perspective on the country.",
  "At Responsible Adventures, we believe a cycling holiday should be about much more than covering kilometres. It's about the people you meet, the villages you pass through, the food you enjoy and the freedom to stop when something catches your eye.",
  "Our carefully designed cycling journeys include gravel biking, mountain biking and e-bike adventures. Whether you're tackling the legendary Annapurna Circuit, exploring the rugged landscapes of Lower Mustang or riding between Kathmandu and Pokhara, we combine rewarding routes with local knowledge, thoughtful pacing and personal attention.",
  "We favour smaller groups, experienced local teams and accommodation with character. Our journeys leave room for spontaneous encounters, cultural discoveries and time to appreciate the places you're travelling through. Good food, responsible travel practices and respect for local communities are integral to how we work.",
  "You bring your curiosity and sense of adventure. We'll take care of the details.",
];

export default function NepalCycling() {
  const [tours, setTours] = useState(null);
  useSeo({
    title: "Nepal Cycling Tours | Gravel, MTB & E-Bike Adventures",
    description: "Explore Nepal by bicycle with Responsible Adventures. Discover gravel, mountain biking and e-bike journeys through Mustang, Annapurna and beyond.",
    path: "/nepal-cycling-tours",
  });
  useEffect(() => {
    window.scrollTo(0, 0);
    getTours({ category: "cycling", destination: "nepal" }).then(setTours).catch(() => setTours([]));
  }, []);

  return (
    <RALayout>
      <section className="max-w-3xl mx-auto px-5 sm:px-8 pt-16 pb-10" data-testid="cycling-intro">
        <p className="font-mono text-xs tracking-[0.25em] uppercase text-terracotta mb-3">Cycling · Nepal</p>
        <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none" data-testid="cycling-title">Cycling Adventures in Nepal</h1>
        <p className="mt-5 text-lg text-ink font-medium">Discover Nepal on two wheels, at your own pace and beyond the usual routes.</p>
        <div className="mt-6 space-y-4 text-ink-secondary leading-relaxed">
          {CYCLING_INTRO.map((p) => <p key={p.slice(0, 20)}>{p}</p>)}
          <p>Explore our Nepal cycling tours below, or <Link to="/contact" className="text-terracotta hover:underline" data-testid="cycling-private-link">talk to us about creating a private cycling journey</Link> around your interests, riding experience and preferred pace.</p>
        </div>
      </section>
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-16">
        {tours === null ? (
          <div className="flex items-center text-ink-secondary py-16"><Loader2 className="w-5 h-5 animate-spin mr-2 text-terracotta" /> Loading…</div>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="cycling-tours">
            {tours.map((t, i) => <TourCard key={t.slug} tour={t} index={i} />)}
          </div>
        )}
      </section>
    </RALayout>
  );
}
